import { createLookup, createMap, ICropping, IField, IPlanInfo, isObject, selectMany } from "../omnia"

export interface Changes {

    Fields: Changeset<IField>
    Cropping: Changeset<ICropping>

    Years: omnia.fields.IFieldYear[]

    Plans: (IPlanInfo & { Cropping: number[] })[]

    HasNutritionRequirements: number[]

}

interface Changeset<T> {
    Keys: number[]
    Resources: T[]
}

export function setupFieldsFromChanges(currentFields: IField[], changes: Changes, partial: boolean) {

    const currentCrops = selectMany(currentFields.map(c => c.Cropping))

    const fields = setupFromChanges(currentFields, changes.Fields, f => f.FieldId, partial)

    const crops = setupFromChanges(currentCrops, changes.Cropping, c => c.CroppingId, partial)

    const cropsLookup = createLookup(crops, c => c.FieldId)
    const yearsLookup = createLookup(changes.Years ?? [], y => y.FieldId)

    const plansLookup = createLookup(
        selectMany((changes.Plans ?? []).map(p => (p.Cropping ?? []).map(c => ({ id: c, plan: p })))),
        a => a.id
    )

    const nreq = new Set(changes.HasNutritionRequirements)

    for (const field of fields) {
        field.Cropping = cropsLookup[field.FieldId] ?? []
        field.Years = yearsLookup[field.FieldId] ?? []
    }

    for (const crop of crops) {

        const plans = plansLookup[crop.CroppingId] ?? []

        crop.Plans = plans.map(p => {
            const { Cropping, ...plan } = p.plan
            return plan
        })

        crop.HasNutritionRequirements = nreq.has(crop.CroppingId)

    }

    return fields

}


function compareChanges(init: IField[], updated: IField[]) {

    function toJson(a: any) {

        function replacer(key: string, value: any) {
            return /Field|Varieties|Crop|SoilType|SubsoilType/.test(key) && isObject(value)
                ? undefined
                : value
        }

        return JSON.stringify(a, replacer, 2)

    }

    localStorage.setItem("___fields.changes.initial", toJson(init))
    localStorage.setItem("___fields.changes.updated", toJson(updated))

}

function setupFromChanges<T>(current: T[], changes: Changeset<T>, keySelector: (a: T) => number, partial: boolean) {

    // Updates and deletions

    const modified = createMap(changes.Resources ?? [], keySelector)
    const available = new Set(changes.Keys ?? [])

    const result = current
        .map(b => {

            const id = keySelector(b)

            if (partial && (!available.has(id))) {
                return
            }

            const u = modified.get(keySelector(b))

            return u ? u : { ...b }

        })
        .filter(r => Boolean(r))

    // Insertions

    const set = new Set(result.map(keySelector))

    for (const n of modified.values()) {

        const id = keySelector(n)

        if (!set.has(id)) {
            result.push(n)
        }

    }

    return result

}
